// All user-facing texts in one place. The worker sends codes; the frontend words them.
import type { BusyReason, DoneStatus, PauseReason, RunStatus, RunTrigger, Schedule } from '@harness/shared/protocol';
import type { Section } from './sections';

export const TEXT = {
  appTitle: 'CloudSpot Harness',
  productName: 'Harness',
  newChat: 'New chat',
  noChats: 'No chats yet.',
  working: 'working …',
  you: 'You',
  assistant: 'Claude · Harness',
  reconnecting: 'Connection lost – reconnecting …',
  signIn: 'Please sign in.',
  signInButton: 'Sign in with Microsoft',
  signOut: 'Sign out',
  noProjects: 'You do not have access to any customer project.',
  noAccess: 'This customer project does not exist, or you do not have access to it.',
  chooseProject: 'Choose customer project',
  customer: 'Customer',
  recentChats: 'Recent chats',
  loading: 'Loading …',
  placeholder: 'Message or command, e.g. /design',
  send: 'Send',
  stop: 'Stop',
  attach: 'Attach file',
  commands: 'Commands',
  dropHere: 'Drop file here',
  removeFile: 'Remove file from message',
  changed: 'Changed:',
  newFile: (path: string): string => `New file: ${path}`,
  presence: (name: string, chatTitle: string): string => `Also active: ${name} (“${chatTitle}”)`,
  automationPresence: (name: string, owner: string): string => `Automation running: “${name}” (${owner})`,
  answer: 'Answer',
  ownAnswer: 'Own answer',
  ownAnswerPlaceholder: 'Your answer',
  answered: 'Answered',
  approvalNeeded: 'Approval needed',
  yes: 'Yes',
  yesForAnswer: 'Yes for this response',
  no: 'No',
  showDetails: 'Details',
  riskyAction: 'This action deletes or overwrites data in Pigment.',
  decided: 'Decided',
  connectTitle: (name: string): string => `Connect ${name}`,
  connectText: (name: string): string => `For this step I need access to ${name} with your account.`,
  connectButton: (name: string): string => `Sign in to ${name}`,
  connectInvalidLink: 'Invalid sign-in link.',
  connectDone: (name: string): string => `${name} sign-in complete.`,
  invalidMessage: 'The message could not be processed.',
  loadFailed: 'Could not be loaded.',
  sections: 'Sections',
  inProgress: 'This feature is in progress.',
  emptyChatTitle: 'What do you want to start with?',
  emptyChat: 'Message or command, e.g. /design – or choose a command below.',
  chatSubtitle: (projectName: string): string => `Vibecoding in Pigment · ${projectName}`,
  noIntegrations: 'No integrations are set up for this customer project.',
  integrationConnected: 'Connected',
  integrationNotConnected: 'Not connected',
  integrationAccess: 'Access',
  integrationSince: 'Connected',
  connect: 'Connect',
  disconnect: 'Disconnect',
  cancel: 'Cancel',
  closeNotice: 'Close notice',
  connectedSince: (date: string): string => `since ${date}`,
  connectedUnknownDate: 'Date unknown',
  disconnectTitle: (name: string): string => `Disconnect ${name}?`,
  disconnectText: (name: string): string =>
    `Claude will no longer be able to work with your account in ${name} in this customer project until you connect again.`,
  connectedNotice: (name: string): string => `${name} is connected.`,
  disconnectedNotice: (name: string): string => `${name} is disconnected.`,
  disconnectFailed: (name: string): string => `${name} could not be disconnected. Please try again.`,
  newAutomation: 'New automation',
  editAutomation: 'Edit automation',
  noAutomations: 'No automations yet. An automation runs a prompt on a schedule, without anyone in the chat.',
  automationName: 'Name',
  automationNamePlaceholder: 'e.g. Forecast check',
  automationPrompt: 'Prompt',
  automationPromptPlaceholder: 'What should Claude do on every run? Commands such as /design work as in the chat.',
  automationSchedule: 'Schedule',
  automationTime: 'Time (Berlin)',
  automationWeekday: 'Weekday',
  allowedTools: 'Allowed writing tools',
  allowedToolsHint:
    'Reading tools and files in the workspace are always available. Writing tools run only if you allow them here; tools that delete or overwrite data can never run in an automation.',
  noWritingTools: 'No writing tools known yet. Connect the integration and use it in a chat once.',
  noneAllowed: 'none',
  owner: 'Owner',
  noOwner: 'no owner',
  lastRun: 'Last run',
  nextRun: 'Next run',
  neverRun: 'Not run yet',
  runNow: 'Run now',
  runStarted: (name: string): string => `“${name}” was started. The run log shows its progress.`,
  runFailedToStart: (name: string): string => `“${name}” could not be started.`,
  edit: 'Edit',
  showRuns: 'Runs',
  details: 'Details',
  delete: 'Delete',
  save: 'Save',
  saveFailed: 'The automation could not be saved.',
  toolNotAllowed: (tool: string): string => `${tool} cannot be allowed for an automation.`,
  takeOver: 'Take over',
  confirmTools: 'Confirm tools',
  toolsConfirmed: 'The allowed tools are confirmed; the automation runs again.',
  takeOverTitle: 'Become the owner?',
  takeOverText: (name: string, owner: string | null): string =>
    `Your change makes you the owner of “${name}”${owner === null ? '' : ` instead of ${owner}`}. It will run with your connections, and commits name you as author. It stays paused until you confirm the allowed tools.`,
  takeOverConfirm: 'Save and become owner',
  takeOverNoOwnerText: (name: string): string =>
    `“${name}” has no owner. As owner it runs with your connections, and commits name you as author. It stays paused until you confirm the allowed tools.`,
  deleteTitle: (name: string): string => `Delete “${name}”?`,
  deleteText: 'The automation and all of its run logs are deleted. Files that runs changed stay in the workspace.',
  deleteFailed: 'The automation could not be deleted.',
  backToAutomations: 'Back to automations',
  noRuns: 'No runs yet.',
  runStart: 'Start',
  runTrigger: 'Trigger',
  runDuration: 'Duration',
  runStatus: 'Status',
  runCost: 'Cost',
  runFiles: 'Files',
  runLog: 'Run log',
  runLogHint: 'Visible to all consultants of this customer project. Data was read with the owner’s access.',
  closeRunLog: 'Close run log',
  attentionNotice: (count: number): string =>
    count === 1 ? 'One automation needs your attention.' : `${count} automations need your attention.`,
  attentionBadge: (count: number): string => `${count} need attention`,
  running: 'Running',
  active: 'Active',
  pausedBy: (name: string): string => `Paused by ${name}`,
  automation: 'Automation',
  automationViews: 'Automations view',
  automationMissing: 'This automation does not exist (any more).',
  runMissing: 'This run does not exist (any more).',
  weekPlan: 'Weekly plan of the automations',
  thisWeek: (week: number): string => `This week · CW ${week}`,
  plannedRun: 'Scheduled run',
  pausedRun: 'Automation paused',
  pauseSwitch: (name: string, active: boolean): string => `${active ? 'Pause' : 'Resume'}: ${name}`,
  pauseFailed: 'The automation could not be switched.',
  uploadData: 'Upload files',
  dataFiles: 'Files',
  dataPeople: 'Uploaded by',
  dataStorage: 'Storage used',
  dataPeopleCount: (count: number): string => (count === 1 ? '1 person' : `${count} people`),
  dataStorageOf: (used: string, quota: string): string => `${used} of ${quota}`,
  dataSearch: 'Search',
  dataSearchPlaceholder: 'File name …',
  dataFile: 'File',
  dataSize: 'Size',
  dataUploadedBy: 'Uploaded by',
  dataDate: 'Date',
  dataActions: 'Actions',
  dataUnknownPerson: 'unknown',
  noDataFiles: 'No files yet. Uploaded files are available to all consultants of this customer and to Claude in every chat and automation.',
  noDataMatches: 'No files match the search.',
  dataDownload: (name: string): string => `Download ${name}`,
  dataDelete: (name: string): string => `Delete ${name}`,
  dataDeleteTitle: (name: string): string => `Delete “${name}”?`,
  dataDeleteText: 'The file is deleted for all consultants of this customer. Chats and automations can no longer read it.',
  dataDeleted: (name: string): string => `“${name}” was deleted.`,
  dataDeleteFailed: (name: string): string => `“${name}” could not be deleted.`,
  dataReplaceTitle: (name: string): string => `Replace “${name}”?`,
  dataReplaceText: 'A file with this name already exists. Replacing it overwrites it for all consultants of this customer.',
  dataReplace: 'Replace',
  dataUploading: (name: string, percent: number): string => `Uploading “${name}” … ${percent} %`,
  dataUploaded: (name: string): string => `“${name}” was uploaded.`,
  newSkill: 'New skill',
  uploadSkill: 'Upload .md',
  skillName: 'Skill',
  skillDescription: 'Description',
  skillChangedBy: 'Last changed by',
  skillDate: 'Changed',
  skillHidden: 'hidden',
  skillHiddenHint: 'A harness or standard skill with this name takes precedence, so this skill cannot be called.',
  noSkills: 'No customer skills yet. A skill is a SKILL.md with instructions that all consultants of this customer can call as /name.',
  skillEdit: (name: string): string => `Edit ${name}`,
  skillDelete: (name: string): string => `Delete ${name}`,
  skillDeleteTitle: (name: string): string => `Delete “${name}”?`,
  skillDeleteText: 'The skill is deleted for all consultants of this customer. Chats and automations can no longer call it.',
  skillDeleted: (name: string): string => `“${name}” was deleted.`,
  skillDeleteFailed: (name: string): string => `“${name}” could not be deleted.`,
  skillSaved: (name: string): string => `“${name}” was saved. It applies from the next message.`,
  skillSaveFailed: 'The skill could not be saved. Please try again.',
  skillNewTitle: 'New skill',
  skillEditTitle: (name: string): string => `Edit /${name}`,
  skillEditorLabel: 'SKILL.md',
  skillEditorHint:
    'Frontmatter fields: name, description, when_to_use, argument-hint, arguments, disable-model-invocation, user-invocable, paths and metadata. "metadata: featured: true" with an optional "chip-hint" shows the skill as a chip above the input field. Shell commands, hooks and allowed-tools are not possible. The name cannot be changed later.',
  skillTooLarge: (name: string): string => `“${name}” is too large (max. 100 KB).`,
  skillNotMarkdown: (name: string): string => `“${name}” is not a .md file.`,
  skillProblems: 'The skill was not saved:',
} as const;

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

/** Weekday names, 1 = Monday … 7 = Sunday, as the worker counts them. */
export function weekdayName(weekday: number): string {
  return WEEKDAYS[weekday - 1] ?? '?';
}

export const scheduleKindText: Record<Schedule['kind'], string> = { daily: 'Daily', weekdays: 'Weekdays', weekly: 'Weekly' };

export function scheduleText(schedule: Schedule): string {
  switch (schedule.kind) {
    case 'daily':
      return `Daily at ${schedule.time}`;
    case 'weekdays':
      return `Monday to Friday at ${schedule.time}`;
    case 'weekly':
      return `${weekdayName(schedule.weekday)}s at ${schedule.time}`;
  }
}

export const runStatusText: Record<RunStatus, string> = {
  running: 'Running',
  ok: 'Succeeded',
  failed: 'Failed',
  limit: 'Limit reached',
  skipped: 'Skipped – still running',
  busy: 'Busy – not run',
  aborted: 'Aborted by a restart',
  missed: 'Missed – server was down',
};

export const tabText: Record<'list' | 'schedule' | 'runs', string> = { list: 'Automations', schedule: 'Schedule', runs: 'Runs' };

export const runTriggerText: Record<RunTrigger, string> = { schedule: 'Schedule', manual: 'Run now' };

export function pauseText(reason: PauseReason, integration: string | null, mine: boolean): string {
  switch (reason) {
    case 'no-owner':
      return 'Paused: no owner. Any consultant can take it over.';
    case 'tools-unconfirmed':
      return mine ? 'Paused: confirm the allowed tools.' : 'Paused: the owner has to confirm the allowed tools.';
    case 'token-missing': {
      const name = integrationText(integration ?? '').name;
      return mine ? `Paused: connect ${name} again on the integrations page.` : `Paused: the owner has to connect ${name} again.`;
    }
  }
}

interface IntegrationText {
  name: string;
  description: string;
  access: string;
  /** Shown on the login card in the chat. */
  connectHint?: string;
}

const INTEGRATIONS: Record<string, IntegrationText> = {
  pigment: {
    name: 'Pigment',
    description: 'Read planning applications and create blocks, formulas and boards.',
    access: 'Read & write (with approval)',
    connectHint: 'Advanced Mode must be turned on in your Pigment account.',
  },
};

/** Texts of one integration; the worker only sends its id. */
export function integrationText(id: string): IntegrationText {
  return INTEGRATIONS[id] ?? { name: id, description: 'Connected system.', access: '–' };
}

export const sectionName: Record<Section, string> = {
  chat: 'Chat',
  data: 'Data',
  skills: 'Skills',
  automations: 'Automations',
  approvals: 'Approvals',
  integrations: 'Integrations',
  chats: 'Manage chats',
};

/** Header of the areas; the chat's header shows the open chat instead. */
export const sectionHeader: Record<Exclude<Section, 'chat'>, { eyebrow: string; title: string; subtitle: string }> = {
  data: { eyebrow: 'Data', title: 'Customer data', subtitle: 'Files of this customer for all consultants – Claude reads them in every chat and automation.' },
  skills: {
    eyebrow: 'Skills',
    title: 'Customer skills',
    subtitle: 'Instructions of this customer for all consultants – callable as /name in every chat and automation.',
  },
  automations: { eyebrow: 'Automations', title: 'Automations & schedule', subtitle: 'Create, schedule and monitor recurring tasks.' },
  approvals: { eyebrow: 'Approvals', title: 'Pending changes', subtitle: 'What automations and chats want to write to Pigment – you decide.' },
  integrations: { eyebrow: 'Integrations', title: 'Integrations & target systems', subtitle: 'Systems in which Claude works with your own account.' },
  chats: { eyebrow: 'History', title: 'Manage chats', subtitle: 'Search, archive and delete chats.' },
};

export function busyText(reason: BusyReason): string {
  switch (reason) {
    case 'chat-busy':
      return 'A response is still running in this chat.';
    case 'capacity':
      return 'Too many responses are running at the same time. Please try again in a moment.';
    case 'not-found':
      return 'This chat no longer exists.';
  }
}

export const doneText: Record<Exclude<DoneStatus, 'ok'>, string> = {
  stopped: 'Stopped.',
  limit: 'The response reached its time or step limit. You can continue with a new message.',
  error: 'An error occurred.',
};

export function uploadErrorText(status: number): string {
  switch (status) {
    case 413:
      return 'The file is too large (max. 50 MB).';
    case 400:
      return 'This file name is not allowed.';
    case 409:
      return 'The file could not be saved.';
    default:
      return 'Upload failed.';
  }
}

/** Why an upload into the customer data failed, from the worker's error code. */
export function dataUploadErrorText(error: string, name: string, allowed: readonly string[], left: string): string {
  switch (error) {
    case 'type':
      return `“${name}” cannot be uploaded: this file type is not supported. Allowed: ${allowed.join(', ')}.`;
    case 'too-large':
      return `“${name}” is too large (max. 500 MB per file).`;
    case 'quota':
      return `“${name}” does not fit: the customer data has only ${left} left.`;
    case 'bad-name':
      return `“${name}” cannot be uploaded: the file name is not allowed.`;
    case 'exists':
      return `“${name}” was not replaced.`;
    default:
      return `“${name}” could not be uploaded. Please try again.`;
  }
}

function words(name: string): string {
  return name.replaceAll('_', ' ');
}

/** "Pigment: create metric" for a tool of an MCP server, named by its integration where known. */
function mcpToolText(server: string, name: string): string {
  return `${integrationText(server).name}: ${words(name)}`;
}

/** Status line for a tool call; an empty string hides the line. */
export function toolText(tool: string, target: string | null): string {
  const where = target === null ? '' : ` ${target}`;
  // The worker describes MCP tools as "<server>:<tool>".
  const mcp = /^([^:]+):(.+)$/.exec(tool);
  if (mcp !== null) return mcpToolText(mcp[1] ?? '', mcp[2] ?? '');
  switch (tool) {
    case 'Read':
    case 'NotebookRead':
      return `reads${where}`;
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
      return `writes${where}`;
    case 'Glob':
    case 'Grep':
    case 'LS':
      return `searches${where}`;
    case 'Bash':
      return 'runs a command';
    case 'Agent':
    case 'Task':
      return `Subagent${target === null ? '' : `: ${target}`}`;
    case 'Skill':
      return `Skill${target === null ? '' : `: ${target}`}`;
    case 'ToolSearch':
    case 'TodoWrite':
    case 'AskUserQuestion':
      return '';
    default:
      return `${tool}${where}`;
  }
}

/** Title of an approval card, e.g. "Pigment: create metric". */
export function approvalTitle(tool: string): string {
  const mcp = /^mcp__(.+?)__(.+)$/.exec(tool);
  return mcp === null ? tool : mcpToolText(mcp[1] ?? '', mcp[2] ?? '');
}
